import React from 'react';
import classNames from 'classnames';
import productPageButtonsContainerStyles from '../ProductPageButtonsContainer.scss';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {Button as ButtonCssVars, ButtonSize, HoverStyles} from 'wix-ui-tpa/cssVars';
import {Button as ButtonLegacy} from 'wix-ui-tpa';
import {ErrorNames, ModalState} from '../../../constants';
import {AddToCartState} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/AddToCartService/constants';
import {IError} from '../../../types/app-types';
import * as buyNowButtonStylesNew from './BuyNowButton.st.css';
import * as buyNowButtonStylesLegacy from './BuyNowButton.legacy.st.css';

export interface BuyNowButtonProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  productInStock: boolean;
}

export enum DataHook {
  buyNowButton = 'buy-now-button',
}

class BuyNowButtonComponent extends React.Component<BuyNowButtonProps> {
  public BuyNowButtonRef = React.createRef<HTMLButtonElement>();

  public componentDidUpdate(prevProps: Readonly<BuyNowButtonProps>): void {
    if (prevProps.globals.modalState === ModalState.OPEN && this.props.globals.modalState === ModalState.CLOSE) {
      this.focusButton();
    }
  }

  public focusButton = () => {
    this.BuyNowButtonRef.current.focus();
  };

  private getCaption(
    t: Function,
    disabled: boolean,
    price: number,
    productInStock: boolean,
    errors: IError,
    addToCartState: AddToCartState
  ): string {
    const isOutOfStockText = price > 0 || !productInStock;
    if (disabled) {
      return isOutOfStockText ? t('PRODUCT_OUT_OF_STOCK_BUTTON') : t('ADD_TO_CART_BUTTON_PRICE_ZERO');
    }

    if (errors.errorName === ErrorNames.StoreInfoError) {
      return 'try again'; // todo(ariell): change to translation
    }

    if (addToCartState === AddToCartState.PRE_ORDER) {
      return t('productPage.preOrderNow.button');
    }

    return t('BUY_NOW_BUTTON');
  }

  public render(): JSX.Element {
    const {
      t,
      productInStock,
      globals: {
        accessibilityEnabled,
        addToCartState,
        errors,
        handleBuyNow,
        product,
        selectedVariant,
        shouldShowAddToCartButton,
        shouldShowWishlistButton,
        experiments,
      },
    } = this.props;

    const useCssVars = experiments.enableStyleProcessorCssVarsMigration;
    const buyNowButtonStyles = useCssVars ? buyNowButtonStylesNew : buyNowButtonStylesLegacy;

    const {price} = selectedVariant || product;
    const disabled = addToCartState !== AddToCartState.ENABLED && addToCartState !== AddToCartState.PRE_ORDER;
    const caption = this.getCaption(t, disabled, price, productInStock, errors, addToCartState);

    const buttonClasses = classNames(buyNowButtonStyles.classes.buyNowButton, {
      [productPageButtonsContainerStyles.primaryButton]: shouldShowWishlistButton && !shouldShowAddToCartButton,
    });

    return useCssVars ? (
      <ButtonCssVars
        data-wix-hideable={true}
        hoverStyle={HoverStyles.underline}
        type="button"
        ref={this.BuyNowButtonRef}
        onClick={() => handleBuyNow(accessibilityEnabled)}
        disabled={disabled}
        data-hook={DataHook.buyNowButton}
        className={buttonClasses}
        size={ButtonSize.large}
        fullWidth={!shouldShowWishlistButton || shouldShowAddToCartButton}>
        {caption}
      </ButtonCssVars>
    ) : (
      <ButtonLegacy
        data-wix-hideable={true}
        type="button"
        ref={this.BuyNowButtonRef}
        onClick={/* istanbul ignore next */ () => handleBuyNow(accessibilityEnabled)}
        disabled={disabled}
        data-hook={DataHook.buyNowButton}
        className={buttonClasses}
        size={ButtonSize.large}
        fullWidth={/* istanbul ignore next */ !shouldShowWishlistButton || shouldShowAddToCartButton}>
        {caption}
      </ButtonLegacy>
    );
  }
}

export const BuyNowButton = withGlobalProps(withTranslations('globals.texts')(BuyNowButtonComponent));
